import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, LockOpen, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Gates the children behind a "foreman password" unlock for employees.
 * Admins always see children. Session-scoped unlock via sessionStorage.
 */
export default function ForemanLock({ children, onUnlocked, title = "Prezzi e Fatture", description }) {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem("iot_foreman_token"));
  const [password, setPassword] = useState("");
  const [opening, setOpening] = useState(false);
  const [showForm, setShowForm] = useState(false);

  if (user?.role === "admin" || unlocked) {
    return (
      <>
        {user?.role !== "admin" && (
          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="foreman-unlocked-badge">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-semibold">Sezione sbloccata come caposquadra</span>
            <button
              onClick={() => {
                sessionStorage.removeItem("iot_foreman_token");
                setUnlocked(false);
              }}
              className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 underline font-semibold"
              data-testid="foreman-lock-button"
            >
              Blocca di nuovo
            </button>
          </div>
        )}
        {children}
      </>
    );
  }

  const unlock = async (e) => {
    e?.preventDefault();
    setOpening(true);
    try {
      const { data } = await api.post("/auth/unlock-foreman", { password });
      sessionStorage.setItem("iot_foreman_token", data.foreman_token);
      setUnlocked(true);
      setPassword("");
      setShowForm(false);
      toast.success("Sezione sbloccata");
      onUnlocked?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOpening(false);
    }
  };

  return (
    <section
      className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl border border-slate-800 p-6 shadow-xl"
      data-testid="foreman-lock-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-sky-500/20 border border-sky-500/30 flex items-center justify-center shrink-0">
          <Lock className="w-6 h-6 text-sky-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-extrabold text-lg">{title}</h3>
          <p className="text-sm text-slate-300 mt-1">
            {description ||
              "Questa sezione è riservata al caposquadra. Inserisci la password per sbloccare prezzo del trasloco e fatture del cliente."}
          </p>
          {!showForm ? (
            <Button
              onClick={() => setShowForm(true)}
              className="mt-4 bg-sky-500 hover:bg-sky-600 text-white font-semibold"
              data-testid="foreman-unlock-reveal-button"
            >
              <LockOpen className="w-4 h-4 mr-2" /> Sblocca sezione
            </Button>
          ) : (
            <form onSubmit={unlock} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-md" data-testid="foreman-unlock-form">
              <div className="flex-1">
                <Label className="text-xs text-slate-400">Password caposquadra</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  className="mt-1 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="foreman-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={opening || !password}
                className="sm:self-end bg-sky-500 hover:bg-sky-600 text-white font-semibold"
                data-testid="foreman-unlock-submit-button"
              >
                {opening ? "…" : "Sblocca"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
